export enum ActivityType {
  RUN = 'Run',
  RIDE = 'Ride',
  SWIM = 'Swim'
}

export enum Division {
  BASIC = 'Cơ bản',
  ADVANCED = 'Nâng cao'
}

// Reactions Enum
export enum ReactionType {
  LOVE = 'love',
  FIRE = 'fire',
  KUDOS = 'kudos'
}

export interface Photo {
  url: string; // Storage URL
  path: string; // Storage Path
  width?: number;
  height?: number;

  // Metadata for fraud detection
  metadata?: {
    originalDate?: string;
    modifyDate?: string; // New
    cameraModel?: string;
    lensModel?: string; // New
    software?: string; // New
    fNumber?: number;
    iso?: number;
    exposureTime?: number;
    gps?: {
      latitude: number;
      longitude: number;
      altitude?: number; // New
    };
  };
}

// Review Status Enum
export type ReviewStatus = 'pending' | 'approved' | 'rejected' | null;

export interface Activity {
  id: string;
  name?: string; // Added optional name
  description?: string; // New: Description
  hasManualName?: boolean; // New: Flag for manual name override
  hasManualDescription?: boolean; // New: Flag for manual description override
  type: ActivityType;
  distance: number; // in km (legacy, rounded to 2 decimals)
  distanceMeters?: number; // in meters (precise, from Strava)
  duration: number; // in minutes (legacy, rounded)
  durationSeconds?: number; // in seconds (precise, from Strava moving_time)
  date: string;
  heartRate?: number;
  points: number;

  // New Flagging System
  reviewStatus?: ReviewStatus;
  flagReason?: string;
  adminReview?: {
    reviewerId: string;
    reviewerName: string;
    note: string;
    timestamp: number;
  };

  stravaId?: string;
  isIndoor?: boolean;
  // Extended Strava Data for Fraud Detection
  maxSpeed?: number; // m/s
  elevationGain?: number; // meters
  averageCadence?: number;
  averageWatts?: number;
  deviceName?: string;
  manual?: boolean;
  isDeleted?: boolean;
  // Additional fraud detection fields
  elapsedTime?: number; // seconds - total time including stops
  averageSpeed?: number; // m/s
  externalId?: string; // source device/app identifier
  commute?: boolean; // marked as commute
  maxHeartrate?: number; // bpm
  // Route visualization
  mapPolyline?: string; // encoded polyline for route SVG

  // Photos
  photos?: Photo[];

  // Social
  reactions?: Record<string, ReactionType>; // userId -> reaction
  reactionCounts?: Record<string, number>;
  commentCount?: number;
  userId?: string; // Augmented when fetching from collection group

  // Group Detection (Phase 1)
  groupId?: string; // e.g., "group_abc123"
  groupMembers?: string[];
  overlapDetails?: Record<string, number>; // activityId -> overlapScore (0-1) // Array of userIds in this group
}

export interface Comment {
  id: string;
  userId: string; // The commenter
  userName: string;
  userAvatar: string;
  content: string;
  createdAt: string; // ISO string
  // Threading & Reactions
  parentId?: string | null;
  reactions?: Record<string, ReactionType>;
  reactionCounts?: Record<string, number>;
  replyCount?: number;
}

export interface Notification {
  id: string;
  recipientId: string;
  senderId: string;
  senderName: string;
  senderAvatar: string;
  type: 'reaction' | 'comment' | 'mixed' | 'system';
  activityId: string;
  activityType: ActivityType; // To show icon
  reactionType?: ReactionType; // Specific reaction if type is 'reaction'
  message: string;
  createdAt: string;
  read: boolean;
  senders?: { id: string; name: string }[];
  activityUserId?: string; // Owner of the activity
}

export interface User {
  id: string;
  name: string;
  avatar: string;
  division: Division;
  teamId?: string;
  activities: Activity[];
  isConnectedStrava: boolean;
  email?: string;
  isAdmin?: boolean;
  isSeeded?: boolean;
  stravaAccessToken?: string;
  stravaRefreshToken?: string;
  stravaTokenExpiresAt?: number;
  totalPoints?: number;
  totalDistance?: number;
  hasInitialSync?: boolean; // Flag to track if full historical sync is done
  lastReadNewsTime?: string; // ISO string of last visit to News tab
  hasCompletedOnboarding?: boolean;
  badgesEarnedOn?: Record<string, string>; // Map: badgeId -> activityId of first activity that earned it
  stats?: UserStats;
  isSuspended?: boolean;
}

export interface UserStats {
  total: {
    Run: StatDetail;
    Ride: StatDetail;
    Swim: StatDetail;
  };
  weekly: Record<string, { // Key: "YYYY-MM-DD" (Monday)
    Run: StatDetail;
    Ride: StatDetail;
    Swim: StatDetail;
  }>;
}

export interface StatDetail {
  distance: number;
  points: number;
}

export interface NewsPost {
  id: string;
  title: string;
  content: string; // Markdown supported
  date: string; // ISO string
  authorName: string;
  authorAvatar?: string;
  isPinned?: boolean;
  imageUrl?: string;
}

export interface Team {
  id: string;
  name: string;
  avatar: string;
  members: string[]; // user IDs
}

export interface SystemSettings {
  eventStartDate?: string; // ISO Date string YYYY-MM-DD
  eventEndDate?: string;   // ISO Date string YYYY-MM-DD
  allowedDomains?: string[]; // List of allowed email domains (e.g., ['techcoop.vn'])
}

export interface FraudAnalysisResult {
  isFraudulent: boolean;
  reason: string;
  confidence: number;
}

export interface AppContext {
  currentUser: User | null;
  users: User[];
  teams: Team[];
  systemSettings: SystemSettings;
  isSyncing: boolean;
  onSync: () => Promise<void>;
  isAdmin: boolean;
}